import type { GuideLesson, GuideModule } from "../types/guide-content.types.ts"
import { isProductTourStepPermitted } from "./product-tour-filter.ts"
import { getAccessibleRecommendedModules } from "./recommended-modules.ts"
import { getGuideModule } from "./modules.ts"
import { getLessonsForModule } from "./lessons.ts"

export interface NextIncompleteLesson {
  lesson: GuideLesson
  module: GuideModule
}

/**
 * Real "Continue Learning" signal: the first lesson, walking this user's
 * accessible modules in Browse-by-Area order, they haven't completed yet —
 * not a literal "resume where you left off" (nothing tracks a lesson as "in
 * progress"), but grounded entirely in the real `completedLessonIds` +
 * permission data, same as the rest of the Guide. Re-derives from the
 * *current* `pages`/`codes` on every call — a lesson started before a
 * permission change is never recommended once it's no longer accessible, and
 * the caller (which recomputes this on every render via `useMemo`) never
 * shows a stale "Continue: X" pointing at something the user can no longer
 * open. Returns null once every accessible lesson is complete (or there
 * simply aren't any yet), which the caller renders as a neutral fallback
 * rather than an empty "Continue Learning" card.
 */
export const getNextIncompleteLesson = (
  pages: readonly string[] | null | undefined,
  codes: unknown,
  completedLessonIds: readonly string[]
): NextIncompleteLesson | null => {
  const moduleSlugs = getAccessibleRecommendedModules(pages)
  for (const slug of moduleSlugs) {
    const module = getGuideModule(slug)
    if (!module) continue
    for (const lesson of getLessonsForModule(slug)) {
      if (completedLessonIds.includes(lesson.id)) continue
      if (!isProductTourStepPermitted(lesson.requiredPage, pages, codes)) continue
      return { lesson, module }
    }
  }
  return null
}
